import { Box, Divider, Typography, useMediaQuery,IconButton } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Badge, CustomButton, CustomRadioGroup } from '../../v2/MUI_custom_component'
import { onboardCompanyRoleAction } from './API/Action'
import CustomChip from '../Components/CustomChip'
import OnboardingContainer from './OnboardingContainer'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import StatusBadge from '../../v2/MUI_custom_component/Badge/StatusBadge'
import IconReusable from '../../components/IconReusable'

const SelectManufacturerRole = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedManufacturer, setExpandedManufacturer] = useState(null);
  const [expandedSelectedManufacturers, setExpandedSelectedManufacturers] = useState([]);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const {
      gst_details, 
      pan_details,
      selected_manufacturer_roles,
      show_gst_input,
      gst_flow
  } = useSelector(state => state.FirmOnboardingReducer || {});
  const ManufacturerCard = ({ 
    manufacturer, 
    selectedRoleId = null, 
    isSelected = false, 
    isExpanded = false, 
    onCardClick, 
    onButtonClick, 
    onRemove, 
    onRoleChange, 
    buttonLabel = "Select Role" 
  }) => {
    const industryLabel = manufacturer.industry?.label || manufacturer.industry;
    const selectedRole = manufacturer.roles.find(role => role.role_id === selectedRoleId);

    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          cursor: onCardClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          position: 'relative',
        }}
        onClick={onCardClick}
      >
        {manufacturer?.badge && (
          <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
            <StatusBadge variant='filled' label={manufacturer?.badge || null} size='small' type='disclaimer' />
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, md: 2 } }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: isSelected ? 'none' : '1px solid #D9D9D9',
                overflow: 'hidden'
              }}
            >
              <Box
                component="img"
                src={manufacturer.logo_url}
                alt={manufacturer.name}
                sx={{ width: '100%' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, md: 1 } }}>
                <Box>
                  <Typography variant="bodyL">
                    {manufacturer.name}
                  </Typography>
                  <Typography variant="body1">
                    {manufacturer.description}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CustomChip
                      size='small'
                      label={industryLabel || 'No industry'}
                      selected={false}
                      variant="outlined"
                    />
                    {isSelected && selectedRoleId && (
                      <CustomChip
                        size='small'
                        label={manufacturer.roles.find(role => 
                          role.role_id === selectedRoleId
                        )?.role_name}
                        selected={true}
                        variant="outlined"
                      />
                    )}
                  </Box>
                  {!isExpanded && onButtonClick && (
                    <CustomButton
                      label={buttonLabel}
                      variant="text"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onButtonClick();
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box>
              {isExpanded && isSelected && <IconButton
                onClick={() => { setExpandedSelectedManufacturers(prev => prev.filter(id => id !== manufacturer.manufacturer_id)) }}
              >
                <IconReusable
                  name="KeyboardArrowUp"
                  size={24}
                  color="#333"
                />
              </IconButton>}
            </Box>
          </Box>
          {isExpanded && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                <Typography variant='h3'>
                  {isSelected ? 'Selected Role' : 'Select Role'}
                </Typography>
                {onRemove && (
                  <CustomButton
                    label="Remove Role"
                    variant="text"
                    size="small"
                    onClick={onRemove}
                  />
                )}
              </Box>
              <CustomRadioGroup
                options={manufacturer.roles.map(role => ({
                  label: role.role_name,
                  value: role.role_id.toString()
                }))}
                size={isMobile ? "small" : "medium"}
                row={false}
                value={selectedRoleId ? selectedRoleId.toString() : ""}
                onChange={(e) => onRoleChange && onRoleChange(parseInt(e.target.value))}
                name={`role-radio-${manufacturer.manufacturer_id}`}
              />
              {selectedRole?.is_gst_required && (
                <Typography 
                  variant='body1'
                  sx={{ color: '#666'}}
                >
                    *GST mandatory for {selectedRole?.role_name} Role
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };
  const loadManufacturers = async () => {
    const { data, error, message } = await onboardCompanyRoleAction();
    if (!error) {
      setManufacturers(data);
    } else {
      toast.error(message);
    }
  };
  const selectManufacturer = (manufacturer) => {
    setExpandedManufacturer(manufacturer);
  };
  const selectRole = (roleId) => {
    if (!expandedManufacturer) return;
    const selectedRole = expandedManufacturer.roles.find(role => role.role_id === roleId);
    if (!selectedRole) return;
    const newSelection = {
      manufacturer: expandedManufacturer,
      roleId: roleId
    };
    let updatedItems;
    const existingIndex = selectedItems.findIndex(item => 
      item.manufacturer.manufacturer_id === expandedManufacturer.manufacturer_id
    );
    
    if (existingIndex >= 0) {
      updatedItems = [...selectedItems];
      updatedItems[existingIndex] = newSelection;
      setSelectedItems(updatedItems);
    } else {
      updatedItems = [...selectedItems, newSelection];
      setSelectedItems(updatedItems);
    }
    dispatch({ 
      type: 'REPLACE_MANUFACTURER_ROLES', 
      payload: updatedItems.map(item => {
        const role = item.manufacturer.roles.find(r => r.role_id === item.roleId);
        return {
          manufacturer_id: item.manufacturer.manufacturer_id,
          manufacturer_name: item.manufacturer.name,
          manufacturer_logo: item.manufacturer.logo_url,
          role_id: item.roleId,
          role_name: role?.role_name,
          is_gst_required: role?.is_gst_required || false
        };
      })
    });
    const hasGstRequired = updatedItems.some(item => {
      const role = item.manufacturer.roles.find(role => role.role_id === item.roleId);
      return role?.is_gst_required === true;
    });
    dispatch({ type: 'SET_SHOW_GST_INPUT', payload: hasGstRequired });
    setExpandedManufacturer(null);
  };
  const removeSelection = (manufacturerId) => {
    const updatedItems = selectedItems.filter(item => 
      item.manufacturer.manufacturer_id !== manufacturerId
    );
    setSelectedItems(updatedItems);
    dispatch({ 
      type: 'REPLACE_MANUFACTURER_ROLES', 
      payload: updatedItems.map(item => {
        const role = item.manufacturer.roles.find(r => r.role_id === item.roleId);
        return {
          manufacturer_id: item.manufacturer.manufacturer_id,
          manufacturer_name: item.manufacturer.name,
          role_id: item.roleId,
          role_name: role?.role_name,
          is_gst_required: role?.is_gst_required || false
        };
      })
    });
    const hasGstRequired = updatedItems.some(item => {
      const selectedRole = item.manufacturer.roles.find(role => role.role_id === item.roleId);
      return selectedRole?.is_gst_required === true;
    });
    
    dispatch({ type: 'SET_SHOW_GST_INPUT', payload: hasGstRequired });
    setExpandedSelectedManufacturers(expandedSelectedManufacturers.filter(id => id !== manufacturerId));
  };
  const toggleSelectedExpansion = (manufacturerId) => {
    setExpandedSelectedManufacturers(prev => {
      if (prev.includes(manufacturerId)) {
        return prev.filter(id => id !== manufacturerId);
      } else {
        return [...prev, manufacturerId];
      }
    });
  };
  const updateSelectedRole = (manufacturerId, roleId) => {
    const updatedItems = selectedItems.map(item => {
      if (item.manufacturer.manufacturer_id === manufacturerId) {
        return { ...item, roleId: roleId };
      }
      return item;
    });
    setSelectedItems(updatedItems);
    dispatch({ 
      type: 'REPLACE_MANUFACTURER_ROLES', 
      payload: updatedItems.map(item => {
        const role = item.manufacturer.roles.find(r => r.role_id === item.roleId);
        return {
          manufacturer_id: item.manufacturer.manufacturer_id,
          manufacturer_name: item.manufacturer.name,
          manufacturer_logo: item.manufacturer.logo_url,
          manufacturer_logo: item.manufacturer.logo_url,
          role_id: item.roleId,
          role_name: role?.role_name,
          is_gst_required: role?.is_gst_required || false
        };
      })
    });
    
    const hasGstRequired = updatedItems.some(item => {
      const selectedRole = item.manufacturer.roles.find(role => role.role_id === item.roleId);
      return selectedRole?.is_gst_required === true;
    });
    
    dispatch({ type: 'SET_SHOW_GST_INPUT', payload: hasGstRequired });
  };
  const hasGST = gst_details?.gst_number && !!gst_details;
  const hasPan = pan_details?.pan_number && !!pan_details;
  const handleContinue = () => {
    
    if(hasGST || (hasPan && !show_gst_input)){
      navigate('/firm-onboarding/confirm-details', {
        state: {
          manu_role: true,
        }
      });
    } else {
      dispatch({ type: 'SET_GST_FLOW', payload: true });
      navigate('/firm-onboarding/onboard-firm');
    }
    
  };
  useEffect(() => {
    loadManufacturers();
  }, []);
  
  useEffect(() => {
    if (selectedItems.length > 0 && expandedSelectedManufacturers.length === 0) {
      setExpandedSelectedManufacturers([selectedItems[0].manufacturer.manufacturer_id]);
    }
  }, [selectedItems.length]);
  useEffect(() => {
    if (manufacturers.length > 0 && selected_manufacturer_roles && selected_manufacturer_roles.length > 0) {
      const reconstructedItems = [];
      
      selected_manufacturer_roles.forEach(savedRole => {
        const manufacturer = manufacturers.find(m => m.manufacturer_id === savedRole.manufacturer_id);
        if (manufacturer) {
          reconstructedItems.push({
            manufacturer: manufacturer,
            roleId: savedRole.role_id
          });
        }
      });
      
      if (reconstructedItems.length > 0) {
        setSelectedItems(reconstructedItems);
      }
    }
  }, [manufacturers, selected_manufacturer_roles]);
  const availableManufacturers = manufacturers.filter(manufacturer => 
    !selectedItems.some(item => 
      item.manufacturer.manufacturer_id === manufacturer.manufacturer_id
    )
  );
  const hasSelectedItems = selectedItems.length > 0;
  const hasAvailableManufacturers = availableManufacturers.length > 0;
   

  return (
    <OnboardingContainer title="Business Info"
      buttonHandler={() => navigate('/firm-onboarding/onboard-firm')}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: {xs: 2, md: 2 } }}>
      {hasSelectedItems && (
        <>
          <Typography variant='h2'>
            Selected Manufacturer & Role
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {selectedItems.map((selection) => {
              const isExpanded = expandedSelectedManufacturers.includes(selection.manufacturer.manufacturer_id);
              return (
                <ManufacturerCard
                  key={selection.manufacturer.manufacturer_id}
                  manufacturer={selection.manufacturer}
                  selectedRoleId={selection.roleId}
                  isSelected={true}
                  isExpanded={isExpanded}
                  buttonLabel="View Role"
                  onButtonClick={() => toggleSelectedExpansion(selection.manufacturer.manufacturer_id)}
                  onRemove={() => removeSelection(selection.manufacturer.manufacturer_id)}
                  onRoleChange={(roleId) => updateSelectedRole(selection.manufacturer.manufacturer_id, roleId)}
                />
              );
            })}
          </Box>
        </>
      )}
      {hasSelectedItems && (
        <Divider sx={{ borderColor: '#D9D9D9'}} />
      )}
      {hasAvailableManufacturers && (
        <>
          <Typography variant='h2'>
            Manufacturer & Role
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {availableManufacturers.map((manufacturer) => {
              const isExpanded = expandedManufacturer?.manufacturer_id === manufacturer.manufacturer_id;
              
              return (
                <ManufacturerCard
                  key={manufacturer.manufacturer_id}
                  manufacturer={manufacturer}
                  isSelected={false}
                  isExpanded={isExpanded}
                  buttonLabel="Select Role"
                  onCardClick={() => selectManufacturer(manufacturer)}
                  onButtonClick={() => selectManufacturer(manufacturer)}
                  onRoleChange={(roleId) => selectRole(roleId)}
                />
              );
            })}
          </Box>
        </>
      )}
      <CustomButton
        label="Continue"
        variant="filled"
        fullWidth
        disabled={selectedItems.length === 0}
        onClick={handleContinue}
        sx = {{
          position: { xs: 'sticky'},
          bottom: 16,
          zIndex: 1000,
        }}
      />
      </Box>
    </OnboardingContainer>
  )
}

export default SelectManufacturerRole